<template>
    <v-form>
        <div class="pb-4">
            <div v-for="(setting, index) in notificationSettings" :key="`${setting.name}-${index}`">
                <template v-if="setting.name !== 'notification.cancel_notification.send.once.per.day'">
                    <template
                        v-if="
                            index === 0 ||
                            (notificationSettings[index - 1].group !== setting.group && isShowGroup(setting.group))
                        "
                    >
                        <div :class="{'pt-4': index !== 0}">{{ getTitle(setting.group) }}</div>
                    </template>
                    <v-checkbox
                        v-if="
                            setting.name !== 'notification.cancel_notification.send.once.per.day' &&
                            isShowGroup(setting.group)
                        "
                        v-model="setting.value"
                        :label="setting.localizableName"
                        density="compact"
                        hide-details
                        :data-cy="setting.group"
                    />
                </template>
                <template v-if="setting.name === 'notification.cancel_notification.send.once.per.day'">
                    <v-radio-group v-model="setting.value" density="compact" hide-details>
                        <v-radio :label="$t('separate_email_about_each_reservation')" :value="false" />
                        <v-radio :label="$t('once_per_day_in_one_mail')" :value="true" />
                    </v-radio-group>
                </template>
            </div>
        </div>
        <v-row no-gutters class="">
            <v-btn :loading="isLoading" :disabled="isLoading" class="btn-save" data-cy="save" @click="save">
                {{ $t('save') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    const props = defineProps<{
        personId?: number
        companyId?: number
        isMyCompanyNotifications?: boolean
        isMyCompanyTouristsNotifications?: boolean
    }>()

    const isLoading = ref<boolean>(false)
    const notificationSettings = ref([])

    onMounted(async () => {
        await loadData()
        EventBus.$on(CHANGE_LOCALE_EVENT, loadData)
    })

    onBeforeUnmount(async () => {
        EventBus.$off(CHANGE_LOCALE_EVENT, loadData)
    })

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const res = await $api[apiMethod.value].get(...apiGetParams.value)
            notificationSettings.value = res
                .sort((a, b) => {
                    if (a.name === 'notification.cancel_notification.send.once.per.day') return 1
                    if (b.name === 'notification.cancel_notification.send.once.per.day') return -1
                    return a.group.localeCompare(b.group)
                })
                .map(item => ({...item, value: item.value === 'true'}))
        } catch (e) {
            notificationSettings.value = []
        } finally {
            isLoading.value = false
        }
    }

    const notificationSettingsGetToPutTransformer = () => {
        const rq = clone(notificationSettings.value)
        return rq.map(setting => {
            const {type, localizableName, group, ...rest} = setting
            return rest
        })
    }

    const save = async () => {
        try {
            isLoading.value = true
            await $api[apiMethod.value].put(...apiPutParams.value)
            await loadData()
            $toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            $toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const getTitle = (group: string): string => {
        switch (group) {
            case 'notification.group.10.orders':
                return t('order_status')
            case 'notification.group.15.new.messages':
                return t('messages_and_attachments')
            case 'notification.group.60.orders':
            case 'notification.group.70.orders':
                return t('unpaid_reservations_before_cancellations_fees')
            case 'notification.group.20.payments':
                return t('description_type.payment')
            case 'notification.group.30.new.entities':
                return t('entities')
            case 'notification.group.40.orders':
                return t('authorization_status')
            case 'notification.group.80.reviews':
                return t('reviews')
            default:
                return ''
        }
    }

    const isShowGroup = (group: string): boolean => {
        const lastEl = notificationSettings.value[notificationSettings.value.length - 1]

        if (group === 'notification.group.70.orders' && !lastEl.value) return false
        return !(group === 'notification.group.60.orders' && lastEl.value)
    }

    const apiMethod = computed(() => {
        if (props.isMyCompanyNotifications) {
            return 'notificationSettingsMyCompany'
        } else if (props.isMyCompanyTouristsNotifications) {
            return 'notificationSettingsMyCompanyClients'
        } else if (props.companyId) {
            return 'notificationSettingsCompany'
        }

        return 'notificationSettingsPerson'
    })

    const apiGetParams = computed(() => {
        if (props.isMyCompanyNotifications) {
            return []
        } else if (props.isMyCompanyTouristsNotifications) {
            return []
        } else if (props.companyId) {
            return [props.companyId]
        }

        return [props.personId]
    })

    const apiPutParams = computed(() => {
        const data = notificationSettingsGetToPutTransformer()

        if (props.isMyCompanyNotifications) {
            return [data]
        } else if (props.isMyCompanyTouristsNotifications) {
            return [data]
        } else if (props.companyId) {
            return [props.companyId, data]
        }

        return [props.personId, data]
    })
</script>
